import { Link } from "react-router-dom";

interface Product {
  id: string;
  icon: string;
  title: string;
  tag?: string;
  description: string;
  features: string[];
}

const bosProducts: Product[] = [
  {
    id: "worktrack",
    icon: "ri-map-pin-user-line",
    title: "WorkTrack",
    tag: "Workforce Management",
    description: "See everything your team is doing, in real time, wherever they work.",
    features: [
      "Staff login, activity and idle tracking",
      "Work mode selection: Office, Remote, Field",
      "Task assignment, deadlines, and progress tracking",
      "Structured daily reporting",
      "Back-and-forth task communication",
      "Escalation management with approval chains",
      "Internal work-based messaging",
      "Customer service ticketing",
      "Sales activity tracking (light CRM)",
      "Productivity insights and performance scoring",
      "GPS and location tracking (admin-controlled toggle)",
      "God's Eye View management dashboard",
    ],
  },
  {
    id: "hris",
    icon: "ri-user-settings-line",
    title: "HCM",
    tag: "Human Resources",
    description: "Manage your people from hire to exit, in one place.",
    features: [
      "Full employee records and lifecycle management",
      "Onboarding and offboarding workflows",
      "Leave and absence management",
      "Document management: contracts, IDs, certificates",
      "Employee self-service portal",
      "Organisational structure and hierarchy mapping",
      "Internal announcements and communications",
      "Queries and disciplinary tracking",
      "Attendance tracking, integrates natively with WorkTrack",
      "Approvals for leave and HR requests",
      "Staff events: birthdays, milestones, announcements",
    ],
  },
  {
    id: "pms",
    icon: "ri-bar-chart-grouped-line",
    title: "PMS",
    tag: "Performance Management",
    description: "Set targets, track performance, and build a culture of accountability.",
    features: [
      "KPI setup using Balanced Scorecard framework",
      "Goal setting and cascading across teams",
      "Monthly and quarterly performance reviews",
      "Manager, peer, and self-assessments",
      "Scoring, ranking, and performance history",
      "Improvement plans (PIP)",
      "Automated performance reports",
    ],
  },
  {
    id: "learn",
    icon: "ri-graduation-cap-line",
    title: "Learning & Development",
    tag: "Training & Knowledge",
    description: "Build the skills your business needs inside the platform your team already uses.",
    features: [
      "Course creation: video, documents, and modules",
      "Onboarding training programmes",
      "Mandatory course assignment by role or department",
      "Quizzes and assessments",
      "Progress tracking and completion reports",
      "Certificate generation",
      "Staff learning dashboard",
    ],
  },
  {
    id: "remoteops-hub",
    icon: "ri-remote-control-line",
    title: "RemoteOps Hub",
    tag: "Control & Execution",
    description: "Central control platform for managing distributed teams, branches, and operations remotely.",
    features: [
      "Multi-location monitoring",
      "Real-time reporting",
      "Operational visibility",
      "Centralized control dashboard",
    ],
  },
  {
    id: "finance",
    icon: "ri-coins-line",
    title: "Accounting",
    tag: "Revenue Management",
    description: "Invoice accurately, track every payment, and stop losing revenue to manual processes.",
    features: [
      "Invoice generation",
      "Rate card configuration per client or service",
      "Automated billing with activity-based triggers",
      "Partial billing logic",
      "Receivables and payment tracking",
      "Revenue dashboards",
      "Client statements",
      "Accounting and financial records",
    ],
  },
  {
    id: "admin",
    icon: "ri-shield-user-line",
    title: "Admin",
    tag: "Internal Operations",
    description: "Bring structure and full visibility to your internal operations.",
    features: [
      "Facility management",
      "Fleet management",
      "Travel management",
      "Procurement management",
      "Asset management",
      "HSE (Health, Safety & Environment)",
      "Service desk",
      "Insurance management",
      "Approvals workflow",
      "Vendor management",
      "Document control",
      "Visitor tracking",
      "Administrative logs",
    ],
  },
  {
    id: "quality-audit",
    icon: "ri-shield-check-line",
    title: "Quality Audit",
    tag: "Compliance & Quality",
    description: "Plan, execute, and close audits without the paperwork.",
    features: [
      "Audit planning and scheduling",
      "Checklist builder",
      "Audit execution and findings tracking",
      "Non-conformance and corrective action management",
      "Escalation system",
      "Audit scoring and compliance reports",
      "Full audit history",
    ],
  },
  {
    id: "growth-marketing",
    icon: "ri-megaphone-line",
    title: "Growth & Marketing",
    tag: "Marketing & Demand Generation",
    description: "Drive awareness, generate demand, and execute campaigns that convert prospects into loyal customers.",
    features: [
      "Campaign Management",
      "Content Planner & Calendar",
      "Lead Capture Forms",
      "Email & SMS Marketing",
      "Audience Segmentation",
      "Marketing Performance Analytics",
      "Marketing Task Management",
      "AI-Assisted Content Suggestions",
    ],
  },
  {
    id: "sales-crm",
    icon: "ri-exchange-dollar-line",
    title: "Sales & CRM",
    tag: "Sales Pipeline & Customer Relationships",
    description: "Build stronger customer relationships and manage your sales pipeline with confidence.",
    features: [
      "Lead Management",
      "Opportunity & Pipeline Tracking",
      "Contact & Account Management",
      "Sales Activity Tracking",
      "Quotations & Proposals",
      "Customer Communication History",
      "Sales Performance Dashboards",
      "Forecasting & Reporting",
      "Team Collaboration Tools",
    ],
  },
  {
    id: "workplace-services",
    icon: "ri-building-4-line",
    title: "Workplace Services",
    tag: "Internal Services & Facilities",
    description: "Create a more organized, efficient, and employee-friendly workplace.",
    features: [
      "Service Request Management",
      "Facility & Maintenance Requests",
      "Visitor Management",
      "Meeting Room Booking",
      "Asset Requests & Allocation",
      "Transportation Requests",
      "Helpdesk & Ticketing",
      "Workplace Announcements",
      "Service Level Monitoring & Reporting",
    ],
  },
];

export default function ProductSections() {
  return (
    <>
      {/* ===== StackMap BOS Section ===== */}
      <section className="py-10 md:py-14">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-16">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8 md:mb-10 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-accent/20 bg-emerald-accent/5 mb-4">
                <span className="text-[10px] sm:text-xs font-semibold text-emerald-accent uppercase tracking-wider">
                  StackMap BOS
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                The Operating System for <span className="text-emerald-accent">Modern Businesses</span>
              </h2>
              <p className="text-base text-silver-400 max-w-2xl mx-auto">
                11 connected modules covering operations, people, performance, finance, and compliance. Pick what you need today, add more when you're ready.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {bosProducts.map((product) => (
                <div
                  key={product.id}
                  className="glass-card p-4 sm:p-5 md:p-7 flex flex-col h-full group"
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-xl bg-graphite-800 group-hover:bg-emerald-accent/10 transition-colors mb-3 sm:mb-5">
                    <i className={`${product.icon} text-xl sm:text-2xl text-emerald-accent`} />
                  </div>
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-1 group-hover:text-emerald-soft transition-colors duration-300">
                    {product.title}
                  </h3>
                  <div className="mb-2 sm:mb-3">
                    {product.tag && (
                      <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-accent/10 text-emerald-accent text-[10px] sm:text-xs font-medium">
                        {product.tag}
                      </span>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-silver-300 leading-relaxed mb-4 sm:mb-5 line-clamp-3">
                    {product.description}
                  </p>
                  <div className="flex-1">
                    <h4 className="text-[10px] sm:text-xs font-semibold text-silver-300 uppercase tracking-wider mb-2 sm:mb-3">
                      Key Features
                    </h4>
                    <ul className="space-y-1.5 sm:space-y-2.5">
                      {product.features.slice(0, 6).map((f) => (
                        <li key={f} className="flex items-start gap-2 sm:gap-2.5 text-xs sm:text-sm text-silver-400">
                          <i className="ri-check-line text-emerald-accent mt-0.5 text-[10px] sm:text-xs shrink-0" />
                          <span className="line-clamp-1 sm:line-clamp-none">{f}</span>
                        </li>
                      ))}
                      {product.features.length > 6 && (
                        <li className="text-[10px] sm:text-xs text-silver-500 italic pl-1">
                          +{product.features.length - 6} more features
                        </li>
                      )}
                    </ul>
                  </div>
                  <Link
                    to={`/products/${product.id}`}
                    className="btn-primary text-center text-xs sm:text-sm mt-4 sm:mt-6 whitespace-nowrap cursor-pointer"
                  >
                    Learn more
                    <span className="w-3 h-3 sm:w-4 sm:h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-[10px] sm:text-xs" />
                    </span>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== OMS — Standalone Section ===== */}
      <section className="py-10 md:py-14 bg-graphite-850">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-16">
          <div className="max-w-5xl mx-auto">
            <div className="mb-8 md:mb-10 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-accent/30 bg-emerald-accent/10 mb-4">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-database-2-line text-emerald-accent text-xs" />
                </span>
                <span className="text-[10px] sm:text-xs font-semibold text-emerald-accent uppercase tracking-wider">
                  Standalone Platform
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Complete Order <span className="text-emerald-accent">Management</span>
              </h2>
              <p className="text-base text-silver-400 max-w-2xl mx-auto">
                A full-featured order management and inventory platform that stands on its own. Built for logistics companies, 3PL operators, warehouses, and FMCG distributors.
              </p>
            </div>

            {/* OMS Showcase Card */}
            <div className="relative overflow-hidden rounded-2xl border border-emerald-accent/20 bg-graphite-900">
              <div className="absolute top-0 right-0 w-72 h-72 bg-emerald-accent/5 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-accent/3 rounded-full blur-3xl pointer-events-none" />

              <div className="relative p-6 sm:p-8 md:p-10">
                <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                  {/* Left: Visual + Badge */}
                  <div className="lg:w-[420px] shrink-0">
                    <div className="rounded-xl overflow-hidden bg-graphite-800 border border-graphite-700 mb-5">
                      <img
                        alt="OMS order management system illustration"
                        className="w-full h-48 sm:h-56 md:h-64 object-cover object-top"
                        src="https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20system%20visualization%20of%20an%20order%20and%20logistics%20pipeline%20with%20flowing%20package%20nodes%20moving%20through%20connected%20system%20layers%2C%20tracking%20path%20lines%2C%20warehouse%20grid%20segments%2C%20inventory%20shelves%2C%20picking%20lanes%2C%20dispatch%20docks%2C%20and%20delivery%20status%20indicators%2C%20clean%20minimal%20dark%20graphite%20background%20with%20subtle%20lighting%2C%20technical%20precise%20render%20style%2C%20no%20text%20no%20logos%2C%20no%20people%2C%20subtle%20emerald%20green%20accent%20highlights%20on%20active%20flow%20paths%2C%20consistent%20lighting%20from%20upper%20left%2C%20sharp%20focus%2C%20high%20detail&width=800&height=500&seq=oms-standalone-showcase&orientation=landscape"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-accent/10 border border-emerald-accent/20 text-emerald-accent text-xs font-medium">
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-truck-line text-xs" />
                        </span>
                        Logistics Companies
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-accent/10 border border-emerald-accent/20 text-emerald-accent text-xs font-medium">
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-store-2-line text-xs" />
                        </span>
                        3PL Operators
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-accent/10 border border-emerald-accent/20 text-emerald-accent text-xs font-medium">
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-building-4-line text-xs" />
                        </span>
                        Warehouses
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-accent/10 border border-emerald-accent/20 text-emerald-accent text-xs font-medium">
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-shopping-basket-line text-xs" />
                        </span>
                        FMCG Distributors
                      </span>
                    </div>
                  </div>

                  {/* Right: Content */}
                  <div className="flex-1 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-emerald-accent/10 border border-emerald-accent/20">
                        <i className="ri-shopping-cart-2-line text-2xl text-emerald-accent" />
                      </div>
                      <div>
                        <h3 className="text-xl sm:text-2xl font-bold text-white">OMS</h3>
                        <p className="text-sm text-silver-400">Order Management System</p>
                      </div>
                    </div>

                    <p className="text-sm sm:text-base text-silver-300 leading-relaxed mb-5">
                      OMS is the operational backbone for logistics companies, 3PL operators, warehouses, FMCG distributors, and retail operations. It provides real-time inventory tracking with SKU-level visibility, structured inbound and outbound workflows, replenishment monitoring, picking and dispatch management, cycle counts, and a merchant and client portal — all with full audit traceability and a complete digital paper trail.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-6">
                      <div>
                        <h4 className="text-xs font-semibold text-silver-400 uppercase tracking-wider mb-3">
                          Inventory & Warehouse
                        </h4>
                        <ul className="space-y-2">
                          {[
                            "Real-time SKU-level inventory tracking",
                            "Multi-location stock management",
                            "Inbound and outbound operations",
                            "Picking and dispatch management",
                            "Cycle counts and stock reconciliation",
                          ].map((f) => (
                            <li key={f} className="flex items-start gap-2 text-xs sm:text-sm text-silver-300">
                              <i className="ri-check-line text-emerald-accent mt-0.5 text-xs shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold text-silver-400 uppercase tracking-wider mb-3">
                          Client & Intelligence
                        </h4>
                        <ul className="space-y-2">
                          {[
                            "Merchant and client self-service portal",
                            "Replenishment tracking and reorder alerts",
                            "Demand signals and forecasting",
                            "SKU and product catalog management",
                            "Complete stock movement history",
                          ].map((f) => (
                            <li key={f} className="flex items-start gap-2 text-xs sm:text-sm text-silver-300">
                              <i className="ri-check-line text-emerald-accent mt-0.5 text-xs shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                      <Link
                        to="/products/oms"
                        className="btn-primary text-sm whitespace-nowrap cursor-pointer"
                      >
                        Explore OMS
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-sm" />
                        </span>
                      </Link>
                      <Link
                        to="/pricing"
                        className="text-sm text-emerald-accent hover:text-emerald-hover transition-colors font-medium whitespace-nowrap"
                      >
                        View pricing →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Get Fit — Vertical SaaS Section ===== */}
      <section className="py-10 md:py-14 bg-graphite-850">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-16">
          <div className="max-w-5xl mx-auto">
            <div className="mb-8 md:mb-10 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-accent/30 bg-emerald-accent/10 mb-4">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-database-2-line text-emerald-accent text-xs" />
                </span>
                <span className="text-[10px] sm:text-xs font-semibold text-emerald-accent uppercase tracking-wider">
                  Vertical SaaS
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Industry-Specific <span className="text-emerald-accent">Solutions</span>
              </h2>
              <p className="text-base text-silver-400 max-w-2xl mx-auto">
                Purpose-built vertical SaaS products for specific industries with unique operational needs.
              </p>
            </div>

            {/* Get Fit Showcase Card */}
            <div className="relative overflow-hidden rounded-2xl border border-emerald-accent/20 bg-graphite-900">
              <div className="absolute top-0 right-0 w-72 h-72 bg-emerald-accent/5 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-accent/3 rounded-full blur-3xl pointer-events-none" />

              <div className="relative p-6 sm:p-8 md:p-10">
                <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                  {/* Left: Visual + Badge */}
                  <div className="lg:w-[420px] shrink-0">
                    <div className="rounded-xl overflow-hidden bg-graphite-800 border border-graphite-700 mb-5">
                      <img
                        alt="Get Fit fitness management platform illustration"
                        className="w-full h-48 sm:h-56 md:h-64 object-cover object-top"
                        src="https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20system%20visualization%20of%20a%20fitness%20management%20dashboard%20with%20heart%20rate%20monitoring%20charts%2C%20class%20schedule%20grids%2C%20member%20progress%20tracking%20rings%2C%20achievement%20badges%20floating%20in%20connected%20wellness%20layers%2C%20gym%20equipment%20nodes%20and%20trainer%20assignment%20cards%20arranged%20in%20a%20structured%20interface%2C%20clean%20minimal%20dark%20graphite%20background%20with%20subtle%20lighting%2C%20technical%20precise%20render%20style%2C%20no%20text%20no%20logos%2C%20no%20people%2C%20subtle%20emerald%20green%20accent%20highlights%20on%20active%20members%2C%20consistent%20lighting%20from%20upper%20left%2C%20sharp%20focus%2C%20high%20detail&width=800&height=500&seq=getfit-vertical-showcase&orientation=landscape"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-accent/10 border border-emerald-accent/20 text-emerald-accent text-xs font-medium">
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-building-4-line text-xs" />
                        </span>
                        Commercial Gyms
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-accent/10 border border-emerald-accent/20 text-emerald-accent text-xs font-medium">
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-user-heart-line text-xs" />
                        </span>
                        Boutique Studios
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-accent/10 border border-emerald-accent/20 text-emerald-accent text-xs font-medium">
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-store-2-line text-xs" />
                        </span>
                        Multi-Location Chains
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-accent/10 border border-emerald-accent/20 text-emerald-accent text-xs font-medium">
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-mental-health-line text-xs" />
                        </span>
                        Wellness Clubs
                      </span>
                    </div>
                  </div>

                  {/* Right: Content */}
                  <div className="flex-1 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-emerald-accent/10 border border-emerald-accent/20">
                        <i className="ri-heart-pulse-line text-2xl text-emerald-accent" />
                      </div>
                      <div>
                        <h3 className="text-xl sm:text-2xl font-bold text-white">Get Fit</h3>
                        <p className="text-sm text-silver-400">Fitness & Wellness Management</p>
                      </div>
                    </div>

                    <p className="text-sm sm:text-base text-silver-300 leading-relaxed mb-5">
                      A comprehensive fitness management platform designed for gyms, fitness studios, and wellness providers. Empower members to stay engaged with their fitness goals while giving operators the tools to manage memberships, trainers, classes, payments, and performance — all from a single ecosystem.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-6">
                      <div>
                        <h4 className="text-xs font-semibold text-silver-400 uppercase tracking-wider mb-3">
                          Member Experience
                        </h4>
                        <ul className="space-y-2">
                          {[
                            "Digital Membership Management",
                            "Mobile Member Portal",
                            "Class Booking & Scheduling",
                            "Progress Tracking & Fitness Insights",
                            "Fitness Challenges & Rewards",
                          ].map((f) => (
                            <li key={f} className="flex items-start gap-2 text-xs sm:text-sm text-silver-300">
                              <i className="ri-check-line text-emerald-accent mt-0.5 text-xs shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold text-silver-400 uppercase tracking-wider mb-3">
                          Gym Management
                        </h4>
                        <ul className="space-y-2">
                          {[
                            "Subscription & Payment Management",
                            "Trainer Management & Tools",
                            "Multi-Branch Administration",
                            "Revenue & Membership Analytics",
                            "Promotional Campaign Management",
                          ].map((f) => (
                            <li key={f} className="flex items-start gap-2 text-xs sm:text-sm text-silver-300">
                              <i className="ri-check-line text-emerald-accent mt-0.5 text-xs shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                      <Link
                        to="/products/get-fit"
                        className="btn-primary text-sm whitespace-nowrap cursor-pointer"
                      >
                        Explore Get Fit
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-sm" />
                        </span>
                      </Link>
                      <Link
                        to="/pricing"
                        className="text-sm text-emerald-accent hover:text-emerald-hover transition-colors font-medium whitespace-nowrap"
                      >
                        View pricing →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}